import {
  Button,
  CodeHighlight,
  Heading,
  Icon,
  Sheet,
  stringify,
  useCopyToClipboard,
  useCurrentOrPrevious,
} from '@dittolive/anvil'
import { CheckIcon, CopyIcon, XIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import upperFirst from 'lodash/upperFirst'
import React from 'react'

import type { PortalApiKey, PortalApiKeyPermissionRule } from '../../../types'

type Props = {
  apiKey?: PortalApiKey
  onClose: () => void
  /**
   * Whether the current user is a super admin (shows remote query field)
   */
  isSuperAdmin?: boolean
}

export default function ApiKeyInspector({
  apiKey,
  onClose,
  isSuperAdmin = false,
}: Props) {
  const displayedApiKey = useCurrentOrPrevious(apiKey)

  const getFriendlyAccessName = (access: PortalApiKeyPermissionRule) => {
    if (access.everything) {
      return 'Full'
    } else if (Object.keys(access.queriesByCollection ?? {}).length > 0) {
      return 'Custom'
    } else {
      return 'Disabled'
    }
  }

  const renderInfo = () => {
    if (!displayedApiKey) {
      return null
    }

    const isExpired = dayjs(displayedApiKey.expiresAt).isBefore(dayjs())

    return (
      <div>
        <InfoLine label="Description" value={displayedApiKey.description} />
        <InfoLine
          label="Created"
          value={dayjs(displayedApiKey.createdAt).format('LL')}
          isContrast
        />
        {displayedApiKey.createdBy && (
          <InfoLine
            label="Created by"
            value={`${displayedApiKey.createdBy.firstName} ${displayedApiKey.createdBy.lastName}`}
          />
        )}
        <InfoLine
          label={isExpired ? 'Expired' : 'Expires'}
          value={dayjs(displayedApiKey.expiresAt).format('LL')}
        />
        {isSuperAdmin && (
          <InfoLine
            label="Remote query"
            value={
              displayedApiKey.permission.remoteQuery ? 'Enabled' : 'Disabled'
            }
            isContrast
          />
        )}
        <InfoLine
          label="Read access"
          value={getFriendlyAccessName(displayedApiKey.permission.read)}
        />
        <InfoLine
          label="Write access"
          value={getFriendlyAccessName(displayedApiKey.permission.write)}
        />
      </div>
    )
  }

  return (
    <Sheet
      open={!!apiKey}
      onOpenChange={(open) => (open ? undefined : onClose())}
    >
      <Sheet.Content
        closeIcon={false}
        overlay={false}
        portal={false}
        className="border-border-normal bg-background-overlay sm:max-w-xl! w-[calc(100vw-2.5rem)] p-0 shadow sm:w-screen"
      >
        <div className="border-b-border-normal flex h-12 w-full items-center border-b">
          <Sheet.Header className="flex-1 space-y-0 text-left">
            <Sheet.Title className="text-foreground-normal mb-1 px-4 font-sans text-base font-medium leading-normal antialiased sm:text-lg">
              API key details
            </Sheet.Title>
            <Sheet.Description className="hidden">
              API key details
            </Sheet.Description>
          </Sheet.Header>

          <div className="flex h-12 w-12 items-center">
            <Button
              size="icon"
              className="hover:bg-background-surface/70 active:bg-background-surface/80 flex h-8 w-8 items-center justify-center border-none"
              onClick={onClose}
            >
              <Icon className="size-4" svg={<XIcon />} />
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {renderInfo()}

          {displayedApiKey && (
            <>
              <ApiKeyPermissionJSON
                kind="read"
                rule={displayedApiKey.permission.read}
              />
              <ApiKeyPermissionJSON
                kind="write"
                rule={displayedApiKey.permission.write}
              />
            </>
          )}
        </div>
      </Sheet.Content>
    </Sheet>
  )
}

type InfoLineProps = {
  label: string
  value: string
  isContrast?: boolean
}

function InfoLine({ label, value, isContrast }: InfoLineProps) {
  return (
    <div
      data-testid={`info-line-${label}-container`}
      className={classNames('flex h-10 items-center justify-between px-4', {
        'bg-background-overlay-hovered': isContrast,
      })}
    >
      <Heading
        level={4}
        data-testid={`info-line-${label}-label`}
        className="text-base"
      >
        {label}
      </Heading>

      <p
        className="text-foreground-normal text-base"
        data-testid={`info-line-${label}-value`}
      >
        {value}
      </p>
    </div>
  )
}

type ApiKeyPermissionProps = {
  kind: 'read' | 'write'
  rule: PortalApiKeyPermissionRule
}

function ApiKeyPermissionJSON({ kind, rule }: ApiKeyPermissionProps) {
  const jsonString = stringify(rule.queriesByCollection, null, 2) || ''
  const [performCopy, recentlyCopied, canCopy] = useCopyToClipboard(jsonString)

  if (rule.everything || Object.keys(rule.queriesByCollection).length === 0) {
    return null
  }

  return (
    <div>
      <div className="bg-background-overlay-hovered flex items-center justify-between p-2">
        <Heading level={4} className="text-base">
          {upperFirst(kind)} queries by collection
        </Heading>

        {canCopy && (
          <span className="text-foreground-normal flex items-center whitespace-nowrap text-sm">
            <Button size="icon" onClick={performCopy}>
              {recentlyCopied ? (
                <Icon
                  className="text-fill-success size-4"
                  svg={<CheckIcon />}
                />
              ) : (
                <Icon
                  className="text-foreground-subtle/80 hover:text-foreground-subtle size-4"
                  svg={<CopyIcon />}
                />
              )}
            </Button>
          </span>
        )}
      </div>

      <CodeHighlight
        code={jsonString}
        language="json"
        lineNumbers
        className="font-plex-mono"
      />
    </div>
  )
}
